import React from 'react';
import { useRecoilValue } from 'recoil';

import { formatExpressionResult } from '@gitbook/expr';
import { Block, type NodeType, type Text } from '@gitbook/slate';
import type { MarkProps, Plugin } from '@gitbook/slate-react';
import {
    DEFAULT_SYNTAX,
    isAvailableLanguage,
    tokenizer,
    useSyntaxHighlighter,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { useExpressionContext } from '../../../Expressions';
import { codeBlockSyntaxesAtom } from '../BlockCode';
import {
    indexPositionedTokensAndInlines,
    type PositionedInline,
    TOKEN_MARK,
    transformTokensToDecorations,
} from './utils';

export function useSyntaxHighlightingPlugin(blockType: string): Plugin {
    const codeBlockSyntaxes = useRecoilValue(codeBlockSyntaxesAtom);
    const { runtime, inputValues } = useExpressionContext();
    const highlighter = useSyntaxHighlighter();
    // handle expressions within code
    const evaluateExpression = useEventCallback((expression: string = '') => {
        if (!expression) {
            return '{{ }}';
        }
        const FALLBACK = `{{${expression}}}`;
        try {
            const expressionValue = runtime.evaluate(expression, inputValues);
            if (!expressionValue) {
                return FALLBACK;
            }
            return formatExpressionResult(expressionValue) || FALLBACK;
        } catch (_) {
            return FALLBACK;
        }
    });
    return React.useMemo(() => {
        if (!highlighter || codeBlockSyntaxes.length === 0) {
            return {};
        }
        return {
            decorateNode: getDecorateNodePlugin(highlighter, blockType, evaluateExpression),
            renderMark,
        };
    }, [blockType, codeBlockSyntaxes, evaluateExpression, highlighter]);
}

/**
 * Plugin to decorate code blocks with syntax highlighting tokens
 * The plugin is memoized based on the loaded syntaxes and highlighter instance.
 */
export function getDecorateNodePlugin(
    highlighter: NonNullable<ReturnType<typeof useSyntaxHighlighter>>,
    blockType: string,
    evaluateExpression: (expression: string) => string | null
) {
    return function decorateNode(node: NodeType) {
        if (!Block.isBlock(node)) {
            return;
        }

        if (node.type !== blockType) {
            return;
        }

        const syntax = swapAlias(node.data.get('syntax'));

        // for the tokenizer to process the tokens we want one text block with newlines
        const { content: blockText, inlines, lines } = getPlainCodeBlock(node, evaluateExpression);

        // use the highlighter to render code syntax highlighting tokens for the code block text
        const { tokens } = tokenizer(highlighter, blockText, syntax);

        // parse tokens and inlines mapping them to an start and end position
        const linesOfPositionedTokens = indexPositionedTokensAndInlines({
            tokens,
            inlines,
            node,
        });

        // transform each of the tokens into a Slate decoration (text range with marks).
        return transformTokensToDecorations({
            lines,
            tokens: linesOfPositionedTokens.flatMap(({ tokens }) => tokens),
        });
    };
}

/**
 * Swap legacy syntax for something Shiki recognises
 **/
export function swapAlias(syntax: string) {
    switch (syntax) {
        case 'objectivec':
            return 'objective-c';
        case 'parser':
            return 'blade';
        default:
            if (isAvailableLanguage(syntax)) {
                return syntax;
            }
            return DEFAULT_SYNTAX;
    }
}

/**
 * Iterate over the nodes in the code block and return their text content with start/end offsets.
 * Extract inlines with start/end offsets.
 */
function getPlainCodeBlock(code: Block, evaluateExpression: (expression: string) => string | null) {
    let allContent = '';
    const inlines: Array<PositionedInline> = [];
    const lines: Array<{ raw: string; text: Text }> = [];
    code.blockNodes.forEach((node, index) => {
        const { content } = getPlainCodeBlockLine(
            node,
            allContent.length,
            inlines,
            lines,
            evaluateExpression
        );
        allContent += content;
        if (index < code.blockNodes.size - 1) {
            allContent += '\n';
        }
    });
    return { content: allContent, inlines, lines };
}

function getPlainCodeBlockLine(
    parentNode: Block,
    startIndex: number,
    inlines: Array<PositionedInline>,
    lines: Array<{ raw: string; text: Text }>,
    evaluateExpression: (expression: string) => string | null
) {
    let allContent = '';
    parentNode.nodes.forEach((node) => {
        if (node.object === 'text') {
            const content = node.leaves
                .map((leaf) => leaf.text)
                .join('')
                .replace(/\r/g, '');
            allContent += content;
            lines.push({ raw: content, text: node });
        } else {
            switch (node.type) {
                case 'annotation': {
                    const start = startIndex + allContent.length;
                    const { content } = getPlainCodeBlockLine(
                        node,
                        start,
                        inlines,
                        lines,
                        evaluateExpression
                    );
                    allContent += content;
                    const end = startIndex + allContent.length;
                    inlines.push({ inline: node, start, end });
                    break;
                }
                case 'expression': {
                    const start = startIndex + allContent.length;
                    const { expression } = node.data.toFrozenObject();
                    const expressionValue = evaluateExpression(expression) ?? '';
                    allContent += expressionValue;
                    const end = startIndex + allContent.length;
                    inlines.push({ inline: node, start, end });
                    const text = node.getFirstText();
                    assert(text);
                    lines.push({ raw: expressionValue, text });
                    break;
                }
                default: {
                    return null;
                }
            }
        }
    });
    return { content: allContent };
}

/**
 * Render a code highlighting token as a Mark in the Slate plugin
 **/
function renderMark(props: MarkProps) {
    const { mark } = props;
    switch (mark.type) {
        case TOKEN_MARK:
            return <CodeHighlightingMark {...props} />;
        default:
            return null;
    }
}

/**
 * React Component to render a code highlighting token.
 */
function CodeHighlightingMark(props: MarkProps) {
    const tokenClasses = props.mark.data.get('classes');
    return (
        <span {...props.attributes} style={toStyles(tokenClasses)}>
            {props.children}
        </span>
    );
}

/**
 * Convert a string of CSS styles into a React style object
 */
function toStyles(value: string) {
    return Object.fromEntries(
        value
            .split(';')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .map((s) => {
                const [key, value] = s.split(':').map((part) => part.trim());
                // Convert key from kebab-case to camelCase
                const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                return [camelKey, value];
            })
    );
}
